<template>
  <div class="bill-wrapper">
    <MobileAccountBill
      v-if="isMobileView"
      :rows="tableData"
      :is-loading="isLoading"
      :is-loading-more="isLoadingMore"
      :load-error="billLoadError"
      :has-more="pageNum * pageSize < total"
      :format-amount="formatOrderAmount"
      @load-more="loadMoreBills"
      @retry="retryBills"
    >
      <template #invoice="{ row }">
          <div class="invoice-actions">
            <div class="invoice-action-slot">
              <el-button
                v-if="isStripeOrder(row) && row.order_status === 1"
                class="mx-1 down-PDF"
                type="primary"
                link
                :loading="row.loading"
                @click="getInvoicePdf(row)"
              >
                {{ row.order_type === 2 ? $t('views.account_center.view_receipt') : $t('views.account_center.download_pdf') }}
              </el-button>
              <el-button
                v-else-if="!isStripeOrder(row) && isInvoiceIssued(row)"
                class="invoice-status-button"
                type="primary"
                link
                :loading="row.invoiceLoading"
                @click="downloadDomesticInvoice(row)"
              >{{ $t('views.account_center.invoice_download') }}</el-button>
              <el-button
                v-else-if="!isStripeOrder(row) && canOpenInvoiceDialog(row)"
                class="invoice-status-button"
                :type="invoiceStatusType(row)"
                link
                @click="openInvoiceDialog(row)"
              >{{ invoiceStatusText(row) }}</el-button>
            </div>
          </div>
      </template>
    </MobileAccountBill>
    <template v-else>
    <!-- 标题 -->
    <div class="title"> <el-text
      size="large"
    >{{ $t('views.account_center.bill') }}</el-text></div>
    <!-- 横线已整合到 title 样式中 -->
    <!-- 骨架加载 -->
    <div
      v-if="isLoading"
      class="table-skeleton"
    >
      <el-skeleton animated>
        <template #template>
          <!-- 表头骨架 -->
          <div class="skeleton-header">
            <el-skeleton-item
              v-for="n in 4"
              :key="'header' + n"
              variant="text"
              class="skeleton-header-item"
            />
          </div>
          <!-- 表格内容骨架 -->
          <div
            v-for="row in 3"
            :key="'row' + row"
            class="skeleton-row"
          >
            <el-skeleton-item
              v-for="col in 4"
              :key="'col' + col"
              variant="text"
              class="skeleton-cell"
            />
          </div>
        </template>
      </el-skeleton>
    </div>
    <!-- 表格 -->
    <el-table
      v-else
      :data="tableData"
      header-cell-class-name="table-header"
      class="bill-custom-table"
    >
      <el-table-column
        prop="date"
        :label="$t('views.account_center.date')"
        width="150"
      />
      <el-table-column
        prop="name"
        :label="$t('views.account_center.plan_name')"
      >
        <template #default="scope">
          {{ scope.row.name }}<span
            v-if="scope.row.order_status !== 1"
          ><el-text
            class="mx-1"
            type="danger"
          >{{ $t('views.account_center.unpaid') }}</el-text></span>
        </template>
      </el-table-column>
      <el-table-column
        :label="$t('views.account_center.amount')"
        width="200"
      >
        <template #default="scope">
          {{ formatOrderAmount(scope.row) }}
        </template>
      </el-table-column>
      <el-table-column
        :label="$t('views.account_center.invoice')"
        width="240"
      >
        <template #default="scope">
          <div class="invoice-actions">
            <div class="invoice-action-slot">
              <el-button
                v-if="isStripeOrder(scope.row) && scope.row.order_status === 1"
                class="mx-1 down-PDF"
                type="primary"
                link
                :loading="scope.row.loading"
                @click="getInvoicePdf(scope.row)"
              >
                {{ scope.row.order_type === 2 ? $t('views.account_center.view_receipt') : $t('views.account_center.download_pdf') }}
              </el-button>
              <el-button
                v-else-if="!isStripeOrder(scope.row) && isInvoiceIssued(scope.row)"
                class="invoice-status-button"
                type="primary"
                link
                :loading="scope.row.invoiceLoading"
                @click="downloadDomesticInvoice(scope.row)"
              >{{ $t('views.account_center.invoice_download') }}</el-button>
              <el-button
                v-else-if="!isStripeOrder(scope.row) && canOpenInvoiceDialog(scope.row)"
                class="invoice-status-button"
                :type="invoiceStatusType(scope.row)"
                link
                @click="openInvoiceDialog(scope.row)"
              >{{ invoiceStatusText(scope.row) }}</el-button>
            </div>
          </div>
        </template>
      </el-table-column>
    </el-table>

    </template>

    <component
      :is="isMobileView ? 'el-drawer' : 'el-dialog'"
      v-model="invoiceDialogVisible"
      class="invoice-dialog"
      :width="isMobileView ? undefined : '920px'"
      :size="isMobileView ? 'min(92vh, 860px)' : undefined"
      :direction="isMobileView ? 'btt' : undefined"
      :show-close="true"
      append-to-body
      @close="handleInvoiceDialogClose"
    >
      <template #header>
        <div class="invoice-dialog-title">
          <span>{{ invoiceDialogTitle }}</span>
          <span class="invoice-dialog-tip">{{ $t('views.account_center.invoice_regular_tip') }}</span>
          <!-- ，专票开具流程 <el-link type="primary" :underline="false" href="#" @click.prevent="showSpecialInvoiceTip">查看这里</el-link> -->
        </div>
      </template>

      <div class="invoice-summary">
        <div class="invoice-summary-item"><span>{{ $t('views.account_center.invoice_amount') }}</span><strong>{{ selectedInvoiceRow ? formatOrderAmount(selectedInvoiceRow) : '-' }}</strong></div>
        <div class="invoice-summary-item"><span>{{ $t('views.account_center.invoice_payment_time') }}</span><b>{{ selectedInvoiceRow && (selectedInvoiceRow.payment_time || selectedInvoiceRow.date) || '-' }}</b></div>
        <div class="invoice-summary-item"><span>{{ $t('views.account_center.invoice_subscription_content') }}</span><b>{{ selectedInvoiceRow && selectedInvoiceRow.name || '-' }}</b></div>
        <div class="invoice-summary-item"><span>{{ $t('views.account_center.invoice_type') }}</span><b>{{ $t('views.account_center.invoice_type_regular') }}</b></div>
        <div class="invoice-summary-item invoice-summary-taxpayer"><span>{{ $t('views.account_center.invoice_taxpayer_type') }}</span><div class="invoice-taxpayer-switch"><button :class="{ active: invoiceForm.taxpayer_type === 'enterprise' }" :disabled="invoiceFormReadOnly || invoiceDetailLoading || invoiceSubmitting" type="button" @click="invoiceForm.taxpayer_type = 'enterprise'">{{ $t('views.account_center.invoice_taxpayer_enterprise') }}</button><button :class="{ active: invoiceForm.taxpayer_type === 'personal' }" :disabled="invoiceFormReadOnly || invoiceDetailLoading || invoiceSubmitting" type="button" @click="invoiceForm.taxpayer_type = 'personal'">{{ $t('views.account_center.invoice_taxpayer_personal') }}</button></div></div>
        <div class="invoice-summary-item"><span>{{ $t('views.account_center.invoice_status') }}</span><b>{{ invoiceStatusText(selectedInvoiceRow) }}</b></div>
      </div>

      <el-divider />

      <div v-if="isInvoiceRejected(selectedInvoiceRow)" class="invoice-reject-alert">
        <strong>{{ $t('views.account_center.invoice_rejection_reason') }}</strong>
        <span>{{ invoiceRejectReason(selectedInvoiceRow) }}</span>
      </div>

      <el-form ref="invoiceFormRef" v-loading="invoiceDetailLoading" :disabled="invoiceDetailLoading || invoiceSubmitting" :model="invoiceForm" :class="{ 'invoice-form-readonly': invoiceFormReadOnly }" class="invoice-form" label-position="left">
        <el-form-item :class="{ 'invoice-form-full': invoiceForm.taxpayer_type === 'enterprise' }">
          <template #label><span class="invoice-field-label"><span class="invoice-required-mark">*</span>{{ $t('views.account_center.invoice_title') }}</span></template>
          <el-input v-model="invoiceForm.invoice_title" :placeholder="invoiceFormReadOnly ? '-' : (invoiceForm.taxpayer_type === 'enterprise' ? $t('views.account_center.invoice_enterprise_title_placeholder') : $t('views.account_center.invoice_personal_title_placeholder'))" :readonly="invoiceFormReadOnly" maxlength="255" />
        </el-form-item>
        <el-form-item v-if="invoiceForm.taxpayer_type === 'enterprise'">
          <template #label><span class="invoice-field-label"><span class="invoice-required-mark">*</span>{{ $t('views.account_center.invoice_tax_no') }}</span></template>
          <el-input v-model="invoiceForm.tax_no" :placeholder="invoiceFormReadOnly ? '-' : $t('views.account_center.invoice_tax_no_placeholder')" :readonly="invoiceFormReadOnly" maxlength="64" />
        </el-form-item>
        <el-form-item>
          <template #label><span class="invoice-field-label"><span class="invoice-required-mark">*</span>{{ $t('views.account_center.invoice_email') }}</span></template>
          <el-input v-model="invoiceForm.email" :placeholder="invoiceFormReadOnly ? '-' : $t('views.account_center.invoice_email_placeholder')" :readonly="invoiceFormReadOnly" maxlength="255" />
        </el-form-item>
        <el-form-item>
          <template #label><span class="invoice-field-label"><span class="invoice-required-mark invoice-required-mark-hidden">*</span>{{ invoiceForm.taxpayer_type === 'enterprise' ? $t('views.account_center.invoice_registered_address') : $t('views.account_center.invoice_contact_address') }}</span></template>
          <el-input v-model="invoiceForm.registered_address" :placeholder="invoiceFormReadOnly ? '-' : (invoiceForm.taxpayer_type === 'enterprise' ? $t('views.account_center.invoice_registered_address_placeholder') : $t('views.account_center.invoice_contact_address_placeholder'))" :readonly="invoiceFormReadOnly" maxlength="500" />
        </el-form-item>
        <el-form-item>
          <template #label><span class="invoice-field-label"><span class="invoice-required-mark invoice-required-mark-hidden">*</span>{{ invoiceForm.taxpayer_type === 'enterprise' ? $t('views.account_center.invoice_registered_phone') : $t('views.account_center.invoice_contact_phone') }}</span></template>
          <el-input v-model="invoiceForm.registered_phone" :placeholder="invoiceFormReadOnly ? '-' : (invoiceForm.taxpayer_type === 'enterprise' ? $t('views.account_center.invoice_registered_phone_placeholder') : $t('views.account_center.invoice_contact_phone_placeholder'))" :readonly="invoiceFormReadOnly" maxlength="64" />
        </el-form-item>
        <el-form-item>
          <template #label><span class="invoice-field-label"><span class="invoice-required-mark invoice-required-mark-hidden">*</span>{{ $t('views.account_center.invoice_bank') }}</span></template>
          <el-input v-model="invoiceForm.bank_name" :placeholder="invoiceFormReadOnly ? '-' : $t('views.account_center.invoice_bank_placeholder')" :readonly="invoiceFormReadOnly" maxlength="255" />
        </el-form-item>
        <el-form-item>
          <template #label><span class="invoice-field-label"><span class="invoice-required-mark invoice-required-mark-hidden">*</span>{{ $t('views.account_center.invoice_bank_account') }}</span></template>
          <el-input v-model="invoiceForm.bank_account" :placeholder="invoiceFormReadOnly ? '-' : $t('views.account_center.invoice_bank_account_placeholder')" :readonly="invoiceFormReadOnly" maxlength="128" />
        </el-form-item>
        <el-form-item class="invoice-form-full"><template #label><span class="invoice-field-label"><span class="invoice-required-mark invoice-required-mark-hidden">*</span>{{ $t('views.account_center.invoice_remark') }}</span></template><el-input v-model="invoiceForm.remark" :placeholder="invoiceFormReadOnly ? '-' : $t('views.account_center.invoice_remark_placeholder')" :readonly="invoiceFormReadOnly" type="textarea" :rows="2" maxlength="500" /></el-form-item>
      </el-form>

      <p class="invoice-privacy"><span>*</span>{{ $t('views.account_center.invoice_privacy') }}</p>
      <template #footer>
        <el-button v-if="canSubmitInvoice" class="invoice-submit-button" type="primary" :loading="invoiceSubmitting" :disabled="invoiceDetailLoading" @click="submitInvoice">{{ isInvoiceRejected(selectedInvoiceRow) ? $t('views.account_center.invoice_resubmit') : $t('views.account_center.invoice_submit') }}</el-button>
        <template v-if="isInvoiceIssued(selectedInvoiceRow)">
          <el-button class="invoice-submit-button invoice-download-button" type="primary" plain :loading="selectedInvoiceRow && selectedInvoiceRow.invoiceLoading" :disabled="invoiceDetailLoading" @click="downloadDomesticInvoice(selectedInvoiceRow)">{{ $t('views.account_center.invoice_download') }}</el-button>
        </template>
      </template>
    </component>

    <!-- 分页器 -->
    <el-pagination
      v-if="!isMobileView"
      :current-page="pageNum"
      :page-sizes="[5, 10]"
      :page-size="pageSize"
      layout="total,prev, pager, next, sizes,jumper"
      :total="total"
      class="custom-pagination"
      size="small"
      :pager-count="5"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />
  </div>
</template>

<script>
import MobileAccountBill from '@/views/mobile/account_center/components/MobileAccountBill.vue'
import { getOrderList, downInvoicePdf, applyInvoice, getInvoiceDetail, downloadInvoice } from '@/api/order'
import { formatCurrency } from '@/utils/currency'

const createInvoiceForm = (orderId = null, detail = {}) => ({
  order_id: orderId,
  invoice_type: detail.invoice_type || 'vat_electronic_normal',
  taxpayer_type: detail.taxpayer_type || 'enterprise',
  invoice_title: detail.invoice_title || '',
  tax_no: detail.tax_no || '',
  email: detail.email || '',
  registered_address: detail.registered_address || '',
  registered_phone: detail.registered_phone || '',
  bank_name: detail.bank_name || '',
  bank_account: detail.bank_account || '',
  remark: detail.remark || ''
})
const INVOICE_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const INVOICE_PHONE_PATTERN = /^(1[3-9]\d{9}|0\d{2,3}-?\d{7,8})$/
const INVOICE_TAX_NO_PATTERN = /^(?:[0-9A-Z]{18}|\d{15})$/

export default {
  components: { MobileAccountBill },
  data() {
    const mobileMediaQuery = window.matchMedia('(max-width: 768px)')
    return {
      mobileMediaQuery,
      isMobileView: mobileMediaQuery.matches,
      billActive: true,
      orderListRequestId: 0,
      invoiceDialogRequestId: 0,
      tableData: [],
      pageNum: 1,
      pageSize: 10,
      total: 0,
      isLoadingMore: false,
      billLoadError: false,
      isLoading: true // 骨架加载
      , invoiceDialogVisible: false
      , invoiceSubmitting: false
      , invoiceDetailLoading: false
      , selectedInvoiceRow: null
      , invoiceDetail: null
      , invoiceForm: createInvoiceForm()
    }
  },
  computed: {
    invoiceFormReadOnly() {
      return Boolean(this.selectedInvoiceRow && !this.hasNoInvoiceApplication(this.selectedInvoiceRow) && !this.isInvoiceRejected(this.selectedInvoiceRow))
    },
    canSubmitInvoice() {
      return Boolean(this.selectedInvoiceRow && (this.hasNoInvoiceApplication(this.selectedInvoiceRow) || this.isInvoiceRejected(this.selectedInvoiceRow)))
    },
    invoiceDialogTitle() {
      if (this.isInvoiceRejected(this.selectedInvoiceRow)) return this.$t('views.account_center.invoice_reapply')
      return this.hasNoInvoiceApplication(this.selectedInvoiceRow) ? this.$t('views.account_center.invoice_apply') : this.$t('views.account_center.invoice_details')
    }
  },
  mounted() {
    this.mobileMediaQuery.addEventListener('change', this.handleMobileViewportChange)
    this.getOrderList()
  },
  beforeUnmount() {
    this.billActive = false
    this.orderListRequestId += 1
    this.invoiceDialogRequestId += 1
    this.mobileMediaQuery.removeEventListener('change', this.handleMobileViewportChange)
  },
  methods: {
    handleMobileViewportChange(event) {
      if (this.isMobileView === event.matches) return
      this.isMobileView = event.matches
      this.pageNum = 1
      this.tableData = []
      this.total = 0
      this.isLoadingMore = false
      this.getOrderList()
    },
    isStripeOrder(row) {
      return Boolean(row && row.payment_channel === 'stripe')
    },
    canOpenInvoiceDialog(row) {
      return Boolean(row && (!this.hasNoInvoiceApplication(row) || row.order_status === 1))
    },
    isInvoiceProcessing(row) {
      return row && (row.invoice_status === 0 || row.invoice_status === 1)
    },
    isInvoiceRejected(row) {
      return Boolean(row && row.invoice_status === 3)
    },
    isInvoiceIssued(row) {
      return Boolean(row && row.invoice_status === 2)
    },
    hasNoInvoiceApplication(row) {
      return row && (row.invoice_status === null || typeof row.invoice_status === 'undefined')
    },
    invoiceRejectReason(row) {
      return String(this.invoiceDetail && this.invoiceDetail.reject_reason || row && row.invoice_reject_reason || this.$t('views.account_center.invoice_rejection_fallback'))
    },
    invoiceStatusType(row) {
      if (this.isInvoiceProcessing(row)) return 'warning'
      if (this.isInvoiceRejected(row)) return 'danger'
      if (this.isInvoiceIssued(row)) return 'success'
      if (row && row.invoice_status === 4) return 'info'
      return 'primary'
    },
    formatOrderAmount(row) {
      return formatCurrency(row.amount, row.currency)
    },
    async getOrderList(append = false) {
      if (!this.billActive) return
      const requestId = ++this.orderListRequestId
      this.billLoadError = false
      if (append) this.isLoadingMore = true
      else this.isLoading = true
      const params = {
        page: append ? this.pageNum + 1 : this.pageNum,
        page_size: this.pageSize
      }
      try {
        const response = await getOrderList(params)
        if (requestId !== this.orderListRequestId) return
        if (!Array.isArray(response?.data?.data)) throw new Error('Invalid order list response')
        const rows = response.data.data
        this.tableData = append ? [...this.tableData, ...rows.filter(row => !this.tableData.some(existing => existing.id === row.id))] : rows
        this.total = Number(response.data.total) || 0
        this.pageNum = params.page
      } catch (error) {
        if (requestId === this.orderListRequestId) this.billLoadError = true
        console.error('加载账单失败:', error)
      } finally {
        if (requestId === this.orderListRequestId) {
          this.isLoading = false
          this.isLoadingMore = false
        }
      }
    },
    loadMoreBills() {
      if (!this.isMobileView || this.isLoading || this.isLoadingMore || this.billLoadError || this.pageNum * this.pageSize >= this.total) return
      this.getOrderList(true)
    },
    retryBills() {
      if (this.isLoading || this.isLoadingMore) return
      this.getOrderList(this.tableData.length > 0)
    },
    async getInvoicePdf(row) {
      if (!this.billActive || !row || row.loading) return
      // 设置当前行的loading状态
      row.loading = true

      try {
        const response = await downInvoicePdf({ order_id: row.id })
        if (!this.billActive) return
        if (response.code === 0 && response.data.has_invoice) {
          if (response.data.type === 'Receipt') {
            // Receipt 类型：打开新窗口查看
            window.open(response.data.invoice_pdf, '_blank')
          } else {
            // Invoice 类型：下载PDF
            this.$message({
              message: this.$t('views.account_center.downloading'),
              type: 'success',
              duration: 2000
            })
            this.$common.download(response.data.invoice_pdf)
          }
        }
      } catch (error) {
        console.error('获取发票失败:', error)
      } finally {
        row.loading = false
      }
    },
    async openInvoiceDialog(row) {
      if (!this.billActive || !row) return
      const requestId = ++this.invoiceDialogRequestId
      this.selectedInvoiceRow = row
      this.invoiceDetail = null
      this.invoiceDetailLoading = false
      this.invoiceForm = createInvoiceForm(row.id)
      this.invoiceDialogVisible = true
      if (this.hasNoInvoiceApplication(row)) return

      this.invoiceDetailLoading = true
      const params = row.invoice_application_id ? { id: row.invoice_application_id } : { order_id: row.id }
      try {
        const response = await getInvoiceDetail(params)
        if (requestId !== this.invoiceDialogRequestId || !this.invoiceDialogVisible) return
        this.invoiceDetail = response.data || {}
        this.invoiceForm = createInvoiceForm(row.id, this.invoiceDetail)
      } catch (error) {
        console.error('加载开票详情失败:', error)
      } finally {
        if (requestId === this.invoiceDialogRequestId) this.invoiceDetailLoading = false
      }
    },
    handleInvoiceDialogClose() {
      if (this.invoiceDialogVisible) return
      this.invoiceDialogRequestId += 1
      this.invoiceDetailLoading = false
    },
    invoiceStatusText(row) {
      if (!row || row.invoice_status === null || typeof row.invoice_status === 'undefined') return this.$t('views.account_center.invoice_apply')
      const statusKey = { 0: 'invoice_status_processing', 1: 'invoice_status_processing', 2: 'invoice_status_issued', 3: 'invoice_status_rejected', 4: 'invoice_status_cancelled' }[row.invoice_status] || 'invoice_apply'
      return this.$t(`views.account_center.${statusKey}`)
    },
    showSpecialInvoiceTip() {
      this.$message.info('专用发票请联系运营管理员处理')
    },
    async submitInvoice() {
      if (!this.billActive || this.invoiceSubmitting || this.invoiceDetailLoading || !this.canSubmitInvoice) return
      const email = String(this.invoiceForm.email || '').trim()
      const phone = String(this.invoiceForm.registered_phone || '').trim()
      const taxNo = String(this.invoiceForm.tax_no || '').trim().toUpperCase()
      if (!this.invoiceForm.invoice_title || !email) {
        this.$message.error(this.$t('views.account_center.invoice_title_email_required'))
        return
      }
      if (!INVOICE_EMAIL_PATTERN.test(email)) {
        this.$message.error(this.$t('views.account_center.email_invalid'))
        return
      }
      if (phone && !INVOICE_PHONE_PATTERN.test(phone)) {
        this.$message.error(this.$t('views.account_center.invoice_phone_invalid'))
        return
      }
      if (this.invoiceForm.taxpayer_type === 'enterprise' && !taxNo) {
        this.$message.error(this.$t('views.account_center.invoice_tax_no_required'))
        return
      }
      if (taxNo && !INVOICE_TAX_NO_PATTERN.test(taxNo)) {
        this.$message.error(this.$t('views.account_center.invoice_tax_no_invalid'))
        return
      }
      this.invoiceForm.email = email
      this.invoiceForm.registered_phone = phone
      this.invoiceForm.tax_no = taxNo
      const form = this.invoiceForm
      const requestId = this.invoiceDialogRequestId
      this.invoiceSubmitting = true
      try {
        await applyInvoice({ ...form })
        if (!this.billActive) return
        this.$message.success(this.$t('views.account_center.invoice_submitted'))
        // A申请完成后不能关闭用户后来打开的B弹窗。
        if (requestId === this.invoiceDialogRequestId && this.invoiceForm === form) this.invoiceDialogVisible = false
        this.getOrderList()
      } catch (error) {
        console.error('提交开票申请失败:', error)
      } finally {
        this.invoiceSubmitting = false
      }
    },
    async downloadDomesticInvoice(row) {
      if (!this.billActive || !row || row.invoiceLoading) return
      row.invoiceLoading = true
      try {
        const response = await downloadInvoice({ id: row.invoice_application_id })
        const data = response.data || {}
        if (this.billActive && data.url) this.$common.download(data.url)
      } catch (error) {
        console.error('下载发票失败:', error)
      } finally {
        row.invoiceLoading = false
      }
    },
    handleSizeChange(val) {
      if (val === this.pageSize) return
      this.pageSize = val
      this.pageNum = 1
      this.getOrderList()
    },
    handleCurrentChange(val) {
      if (val === this.pageNum) return
      this.pageNum = val
      this.getOrderList()
    }
  }
}
</script>
<style lang="scss">
@import '@/assets/css/variables';
.bill-custom-table {
  .el-table__header-wrapper thead th {
    color: var(--agst-text-color-primary,$text-color-primary);
    text-align: center;
  }
  .el-table__body-wrapper tbody td {
    border: none;
    text-align: center;
    height: 40px;
  }
}
.invoice-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  width: 100%;
  white-space: nowrap;
}
.invoice-action-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}
.invoice-actions .el-button {
  margin-left: 0;
  margin-right: 0;
}
.invoice-status-button {
  min-width: 96px;
}
.invoice-status-text {
  line-height: 24px;
  white-space: nowrap;
}
.invoice-reject-alert {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: -8px 0 24px;
  padding: 12px 16px;
  border: 1px solid #fbc4c4;
  border-radius: 6px;
  background: #fef0f0;
  color: #f56c6c;
  font-size: 14px;
  line-height: 22px;
}
.invoice-reject-alert strong {
  flex: 0 0 auto;
  color: #f23030;
}
.invoice-reject-alert span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.invoice-dialog .el-dialog {
  border-radius: 18px;
  overflow: hidden;
}
.invoice-dialog .el-dialog__header {
  padding: 28px 32px 12px;
  margin-right: 0;
}
.invoice-dialog .el-dialog__body {
  padding: 0 32px 8px;
}
.invoice-dialog .el-dialog__footer {
  padding: 16px 32px 28px;
}
.invoice-dialog-title {
  color: #1f2937;
  font-size: 24px;
  font-weight: 700;
  line-height: 34px;
}
.invoice-dialog-tip {
  color: #1f2937;
  font-size: 14px;
  font-weight: 400;
  margin-left: 4px;
}
.invoice-dialog-tip .el-link {
  color: #7c4dce;
  vertical-align: baseline;
}
.invoice-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 68px;
  row-gap: 20px;
  padding: 20px 0 18px;
}
.invoice-summary-item {
  display: flex;
  align-items: center;
  min-height: 28px;
  color: #9aa1ad;
  font-size: 16px;
}
.invoice-summary-item > span:first-child {
  flex: 0 0 84px;
}
.invoice-summary-item strong {
  color: #17191d;
  font-size: 22px;
}
.invoice-summary-item b {
  color: #292d33;
  font-weight: 400;
}
.invoice-summary-taxpayer {
  align-items: center;
}
.invoice-taxpayer-switch {
  display: flex;
  gap: 14px;
}
.invoice-taxpayer-switch button {
  border: 0;
  border-radius: 8px;
  background: #f4f4f5;
  color: #2d3138;
  cursor: pointer;
  font-size: 16px;
  padding: 10px 18px;
}
.invoice-taxpayer-switch button.active {
  background: #fff;
  border: 1px solid #252525;
  padding: 9px 17px;
}
.invoice-taxpayer-switch button:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}
.invoice-dialog .el-divider {
  margin: 0 0 24px;
}
.invoice-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 42px;
}
.invoice-form .el-form-item {
  margin-bottom: 16px;
}
.invoice-form .el-form-item__label {
  box-sizing: border-box;
  color: #9aa1ad;
  font-size: 16px;
  height: auto;
  padding-right: 10px;
  white-space: normal;
  width: 88px !important;
}
.invoice-form .el-form-item__content {
  margin-left: 0 !important;
}
.invoice-field-label {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
}
.invoice-required-mark {
  display: inline-block;
  flex-shrink: 0;
  width: 10px;
  color: #f56c6c;
  line-height: 1;
  text-align: left;
}
.invoice-required-mark-hidden {
  visibility: hidden;
}
.invoice-form .invoice-form-full {
  grid-column: 1 / -1;
}
.invoice-form .el-input__wrapper,
.invoice-form .el-textarea__inner {
  background: #f5f6f8;
  border-radius: 8px;
  box-shadow: none;
}
.invoice-form .el-input__wrapper {
  box-sizing: border-box;
  height: 40px;
  min-height: 40px;
  padding: 0 12px;
}
.invoice-form .el-textarea__inner {
  padding: 10px 12px;
}
.invoice-form .el-input__inner,
.invoice-form .el-textarea__inner {
  color: #292d33;
  font-size: 16px;
}
.invoice-form .el-input__inner::placeholder,
.invoice-form .el-textarea__inner::placeholder {
  color: #a8adb7;
}
.invoice-form-readonly .el-input__wrapper,
.invoice-form-readonly .el-textarea__inner {
  background: #fff;
  border: 1px solid #ebeef5;
}
.invoice-privacy {
  color: #a3a9b4;
  font-size: 14px;
  line-height: 22px;
  margin: 2px 0 0;
}
.invoice-privacy span {
  color: #7c4dce;
  margin-right: 3px;
}
.invoice-cancel-button,
.invoice-submit-button {
  border-radius: 10px;
  font-size: 16px;
  height: 48px;
  min-width: 86px;
}
.invoice-submit-button {
  background: #252525;
  border-color: #252525;
  min-width: 120px;
}
.invoice-submit-button:hover,
.invoice-submit-button:focus {
  background: #111;
  border-color: #111;
}

/* 开票表单按桌面屏幕宽度适配输入框尺寸和行间距。 */
@media (min-width: 1360px) and (max-width: 1439px) {
  .invoice-form .el-form-item {
    margin-bottom: 20px;
  }
  .invoice-form .el-input__wrapper {
    padding-left: 14px;
    padding-right: 14px;
  }
  .invoice-form .el-textarea__inner {
    padding-left: 14px;
    padding-right: 14px;
  }
}
@media (min-width: 1440px) {
  .invoice-form .el-form-item {
    margin-bottom: 24px;
  }
  .invoice-form .el-input__wrapper {
    height: 44px;
    min-height: 44px;
    padding-left: 16px;
    padding-right: 16px;
  }
  .invoice-form .el-textarea__inner {
    padding-left: 16px;
    padding-right: 16px;
  }
}
@import '@/views/mobile/account_center/styles/invoice_dialog';
</style>
<style scoped lang="scss">
@import '@/assets/css/views/account_center/components/bill';
</style>
